/**
 * Web (Browser) platform adapter.
 *
 * Runs in any modern browser. No native APIs, no custom title bar.
 * Uses localStorage for storage and the Web Notifications API.
 */
import { PlatformAdapter } from "./platform.interface";
import { createStableWebDeviceId } from "./uuid";

export class WebPlatform implements PlatformAdapter {
  readonly type = "web" as const;
  readonly supportsCustomTitleBar = false;
  readonly supportsAmiBroker = false;
  readonly maxChartPanels = 4;
  readonly fullDrawingTools = true;
  readonly supportsLocalBacktest = true;
  readonly tickBatchIntervalMs = 100;

  // ─── Device Identity ─────────────────────────────────

  async getDeviceId(): Promise<string> {
    const stableId = createStableWebDeviceId();
    const existing = localStorage.getItem("_web_device_id");

    // Migrate old origin-scoped random IDs so localhost and LAN-IP share
    // the same logical device identity.
    if (existing !== stableId) {
      localStorage.setItem("_web_device_id", stableId);
    }

    return stableId;
  }

  getDeviceName(): string {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome")) return "Chrome Browser";
    if (ua.includes("Firefox")) return "Firefox Browser";
    if (ua.includes("Safari")) return "Safari Browser";
    if (ua.includes("Edge")) return "Edge Browser";
    return "Web Browser";
  }

  // ─── Window Management (no-ops) ──────────────────────

  async minimizeWindow(): Promise<void> {
    /* no-op */
  }
  async maximizeWindow(): Promise<void> {
    /* no-op */
  }
  async closeWindow(): Promise<void> {
    /* no-op */
  }

  // ─── Storage ─────────────────────────────────────────

  async getStorageItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  }

  async setStorageItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  }

  async removeStorageItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  }

  // ─── Notifications ───────────────────────────────────

  async showNotification(title: string, body: string): Promise<void> {
    if (Notification.permission === "granted") {
      new Notification(title, { body });
    }
  }

  async requestNotificationPermission(): Promise<boolean> {
    const perm = await Notification.requestPermission();
    return perm === "granted";
  }

  async getPushToken(): Promise<string | null> {
    // Web push requires service worker + VAPID — future enhancement
    return null;
  }

  // ─── Lifecycle Events ────────────────────────────────

  onAppClose(callback: () => void): () => void {
    const handler = (e: BeforeUnloadEvent) => {
      callback();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }

  onAppResume(callback: () => void): () => void {
    const handler = () => {
      if (document.visibilityState === "visible") callback();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }

  // ─── OAuth / Auth ─────────────────────────────────────────────────────────

  getOAuthClientId(): string {
    // Web-specific client ID injected via the Vite HTML shim (REACT_APP_OAUTH_CLIENT_ID_WEB).
    // Set in .env (dev) or .env.production (prod). Fallback matches application[-prod].properties.
    return (
      process.env.REACT_APP_OAUTH_CLIENT_ID_WEB ||
      process.env.REACT_APP_OAUTH_CLIENT_ID ||
      process.env.REACT_APP_CLIENTID ||
      "marketview-web-client"
    );
  }

  getOAuthRedirectUri(): string {
    // Web: use the explicitly configured redirect URI if set, otherwise derive
    // from the current origin so it always matches the loaded URL.
    return (
      process.env.REACT_APP_OAUTH_REDIRECT_URI_WEB ||
      process.env.REACT_APP_OAUTH_REDIRECT_URI ||
      `${window.location.origin}/`
    );
  }

}
